import {Injectable} from '@angular/core';
import {COOKIES_ACCEPTED, getCookie, setCookie} from "../../umbrella-common/common-functions";
import {BehaviorSubject, Observable} from "rxjs";
import {ConfigurationProvider} from "../../configuration/configuration.provider";
import {first, map, reduce, tap} from "rxjs/operators";
import {CookiesConfig, CookieSettings} from "../../configuration/configuration.model";
import {CookiePolicyFactoryService} from "./cookie-policy-factory.service";

@Injectable({
	providedIn: 'root'
})
export class CookiesSettingsService {

	private readonly localStorageCookiePrefix: string = "cookies.";
	private readonly localStorageCookieSuffix: string = ".accepted";

	private readonly items$: Observable<CookiesConfig>;
	private cookiesIdToFilesNameMap$: Observable<{ [id: string]: string[] }>;
	private cookiePolicyFactory$: Observable<CookiePolicyFactoryService>;
	private cookiesStateSubject: BehaviorSubject<CookiesState> = new BehaviorSubject(null);
	private cookiesBannerVisible: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private setCookiesOnStartup: boolean;

	constructor(private configProvider: ConfigurationProvider) {
		this.items$ = this.configProvider.getConfiguration().pipe(
			first(),
			tap(config => this.setCookiesOnStartup = config.setCookiesOnStartup),
			map(config => {
				return {
					mandatory: config.cookieSettings.mandatory,
					comfort: config.cookieSettings.comfort,
					statistics: config.cookieSettings.statistics,
					marketing: config.cookieSettings.marketing,
				};
			})
		);

		this.cookiesIdToFilesNameMap$ = this.items$.pipe(
			map(this.toCookiesNames()),
			reduce((obj, item) => Object.assign(obj, ...item), {}),
		);

		this.cookiePolicyFactory$ = this.cookiesIdToFilesNameMap$.pipe(map(cookiesIdToFilesNameMap => {
			const cookiePolicyFactoryService = new CookiePolicyFactoryService();
			cookiePolicyFactoryService.setCookiesIdsAndNames(cookiesIdToFilesNameMap)
			return cookiePolicyFactoryService;
		}));
	}

	private toCookiesNames() {
		return cookie => {
			let array = [];
			const keys = Object.keys(cookie);
			keys.forEach(key => array.push(cookie[key].map(this.extractCookiesNames())));
			return array.flat();
		};
	}

	private extractCookiesNames() {
		return cookie => ({
			[cookie.id]: cookie.details.find(detail => detail.key === 'name').value
		});
	}

	getCookies() {
		return this.items$;
	}

	acceptAll() {
		this.setCookiesValue(true);
	}

	rejectAll() {
		this.setCookiesValue(this.setCookiesOnStartup);
	}

	private setCookiesValue(accepted: boolean) {
		this.cookiesIdToFilesNameMap$.pipe(map(cookiesIdToFilesNameMap => {
			let ids = Object.keys(cookiesIdToFilesNameMap);
			return ids.reduce((obj, id) => {
				obj[id] = accepted;
				return obj;
			}, {});
		}), first()).subscribe(cookiesMap => this.handleCookies(cookiesMap));
	}

	handleCookies(cookies: {[cookieId: string] : boolean}, initial: boolean = false) {
		Object.entries(cookies).forEach(([id, accepted]) => localStorage.setItem(`${this.localStorageCookiePrefix}${id}${this.localStorageCookieSuffix}`, accepted.toString()))

		this.cookiePolicyFactory$.pipe(first()).subscribe(cookiePolicyFactory => {
			const ids = Object.keys(cookies);
			ids.forEach(id => cookiePolicyFactory.handle(id, cookies[id]));
		});

		if (!initial) {
			let allAccepted = Object.values(cookies).every(val => val === true);
			let allRejected = Object.values(cookies).every(val => val === false);

			if (allAccepted) {
				this.cookiesStateSubject.next(CookiesState.ACCEPTED);
				this.handleGlobalFlag(true);
			}

			if (allRejected) {
				this.cookiesStateSubject.next(CookiesState.REJECTED);
				this.handleGlobalFlag(false);
			}
		}
	}

	handleCookiesAtAppStart() {
		this.getStoredCookiesConsents().subscribe(cookiesMap => {
			this.acceptCookiesForCountry(cookiesMap);
			this.handleCookies(cookiesMap, true);
		});
	}

	private acceptCookiesForCountry(cookiesMap: { [p: string]: boolean }) {
		if (this.setCookiesOnStartup && getCookie(COOKIES_ACCEPTED) !== '0') {
			Object.keys(cookiesMap).forEach(name => {
					cookiesMap[name] = true;
					return;
			});
			this.handleGlobalFlag(true);
		}
	}

	cookiesAccepted(): boolean {
		return getCookie(COOKIES_ACCEPTED) !== '1';
	}

	private handleGlobalFlag(accepted: boolean) {
		setCookie(COOKIES_ACCEPTED, accepted ? '1' : '0', 365 * 100, "/");
	}

	allAccepted(): Observable<CookiesState> {
		return this.cookiesStateSubject.asObservable();
	}

	isCookieAccepted(id: string): boolean {
		return localStorage.getItem(`${this.localStorageCookiePrefix}${id}${this.localStorageCookieSuffix}`) === 'true';
	}

	isGroupAccepted(cookieGroup: CookieSettings[]): boolean {
		if (this.isGroupEmpty(cookieGroup)) {
			return false;
		}

		return cookieGroup.some(group => this.isCookieAccepted(group.id));
	}

	private getStoredCookiesConsents(): Observable<{[id: string]: boolean}> {
		return this.cookiesIdToFilesNameMap$.pipe(
			map(cookieMap => Object.keys(cookieMap)),
			map(ids => ids.reduce((obj, id) => {
				obj[id] = localStorage.getItem(`${this.localStorageCookiePrefix}${id}${this.localStorageCookieSuffix}`) === "true"
				return obj;
			}, {})),
			first()
		);
	}

	isGroupEmpty(cookieGroup: CookieSettings[]) {
		return cookieGroup.length === 0;
	}

	onCookieBannerInitialized(): void {
		this.cookiesBannerVisible.next(true);
	}

	onCookieBannerDestroyed(): void {
		this.cookiesBannerVisible.next(false);
	}

	isCookieBannerVisible(): Observable<boolean> {
		return this.cookiesBannerVisible.asObservable();
	}
}

export enum CookiesState {
	ACCEPTED, REJECTED
}
